import { useAuthStore } from '@sentinel/web-core';
import {
  createRootRoute,
  createRoute,
  createRouter,
  Outlet,
  redirect,
} from '@tanstack/react-router';
import { CallbackPage } from '@/auth/CallbackPage';
import { StartPage } from '@/auth/StartPage';
import { AuditPage } from '@/modules/audit/AuditPage';
import { OrganizationPage } from '@/modules/organizations/OrganizationPage';
import { OrganizationsPage } from '@/modules/organizations/OrganizationsPage';
import { ModuleRequestsPage } from '@/modules/requests/ModuleRequestsPage';
import { PlatformSettingsPage } from '@/modules/settings/PlatformSettingsPage';
import { SupportPage } from '@/modules/support/SupportPage';
import { TeamPage } from '@/modules/team/TeamPage';
import { ConsoleShell } from '@/shell/ConsoleShell';

const rootRoute = createRootRoute({ component: Outlet });

const startRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/auth/start',
  component: StartPage,
});
const callbackRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/auth/callback',
  component: CallbackPage,
  validateSearch: (search: Record<string, unknown>): { code?: string } =>
    typeof search['code'] === 'string' ? { code: search['code'] } : {},
});

/** Only a console session opens the console; anything else signs in again through `accounts.`. */
const shellRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: 'console',
  component: ConsoleShell,
  beforeLoad: () => {
    const { status, me } = useAuthStore.getState();
    if (status !== 'authenticated' || me?.context !== 'console')
      throw redirect({ to: '/auth/start' });
  },
});

const homeRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: '/',
  component: OrganizationsPage,
});
const organizationRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: '/organizations/$organizationId',
  component: OrganizationPage,
});
const requestsRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: '/requests',
  component: ModuleRequestsPage,
});
const supportRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: '/support',
  component: SupportPage,
});
const teamRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: '/team',
  component: TeamPage,
});
const auditRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: '/audit',
  component: AuditPage,
});
const settingsRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: '/settings',
  component: PlatformSettingsPage,
});

const routeTree = rootRoute.addChildren([
  startRoute,
  callbackRoute,
  shellRoute.addChildren([
    homeRoute,
    organizationRoute,
    requestsRoute,
    supportRoute,
    teamRoute,
    auditRoute,
    settingsRoute,
  ]),
]);

export const router = createRouter({
  routeTree,
  defaultPreload: 'intent',
  scrollRestoration: true,
  // The window does not scroll: the main area of the shell does (shell.css).
  scrollToTopSelectors: ['main.area'],
});

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}
